import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Box, Button, Checkbox, Chip, CircularProgress, FormControlLabel, IconButton, LinearProgress, MenuItem, Radio, Select, Stack, TextField, Typography } from '@mui/material';
import ArrowUpward from '@mui/icons-material/ArrowUpward';
import ArrowDownward from '@mui/icons-material/ArrowDownward';
import CheckCircle from '@mui/icons-material/CheckCircle';
import Cancel from '@mui/icons-material/Cancel';
import Replay from '@mui/icons-material/Replay';
import QuizOutlined from '@mui/icons-material/QuizOutlined';
import ShortText from '@mui/icons-material/ShortText';
import SwapHoriz from '@mui/icons-material/SwapHoriz';
import SwapVert from '@mui/icons-material/SwapVert';
import StyleOutlined from '@mui/icons-material/StyleOutlined';
import PollOutlined from '@mui/icons-material/PollOutlined';
import { useTranslate } from 'react-admin';
import { blockInteractionService, type BlockInteractionResult, type PollResults } from '../../../../services/blockInteractionService';
import { useBlockInteraction } from './BlockInteractionContext';

/* ───────────────────────── shared ───────────────────────── */

const Head: React.FC<{ icon: React.ReactNode; title: string; right?: React.ReactNode }> = ({ icon, title, right }) => (
    <div className="oa-bp-card-head">
        <span className="oa-bp-icon">{icon}</span>
        <span className="oa-bp-grow">{title}</span>
        {right}
    </div>
);

const ResultBanner: React.FC<{ result: BlockInteractionResult | null; explanation?: string }> = ({ result, explanation }) => {
    const translate = useTranslate();
    if (!result || result.isCorrect === null) return null;
    return (
        <Alert severity={result.isCorrect ? 'success' : 'warning'} icon={result.isCorrect ? <CheckCircle fontSize="inherit" /> : <Cancel fontSize="inherit" />} sx={{ mt: 1.5 }}>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {result.isCorrect ? translate('blockPlayer.result.correct') : translate('blockPlayer.result.incorrect', { percent: Math.round(result.scorePercent ?? 0) })}
            </Typography>
            {result.feedback && <Typography variant="body2">{result.feedback}</Typography>}
            {explanation && <Typography variant="body2" className="oa-bp-plain" sx={{ mt: 0.5 }}>{explanation}</Typography>}
            <Typography variant="caption" color="text.secondary">{translate('blockPlayer.result.attempt', { n: result.attemptNumber })}</Typography>
        </Alert>
    );
};

/**
 * Submits a response for the block; in previews (no lesson) nothing is sent and
 * the caller gets null.
 */
function useSubmit(blockId: string, blockType: string) {
    const { lessonId, readOnly, onInteraction } = useBlockInteraction();
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [result, setResult] = useState<BlockInteractionResult | null>(null);
    const translate = useTranslate();

    const submit = useCallback(
        async (response: Record<string, unknown>) => {
            if (lessonId === null || readOnly) return null;
            setBusy(true);
            setError(null);
            try {
                const r = await blockInteractionService.submit(lessonId, blockId, response);
                setResult(r);
                onInteraction?.(blockId, r);
                return r;
            } catch {
                setError(translate('blockPlayer.result.error'));
                return null;
            } finally {
                setBusy(false);
            }
        },
        [lessonId, readOnly, blockId, onInteraction, translate],
    );

    const reset = useCallback(() => {
        setResult(null);
        setError(null);
    }, []);

    return { submit, busy, error, result, reset, canSubmit: lessonId !== null && !readOnly, blockType };
}

const PreviewHint: React.FC = () => {
    const translate = useTranslate();
    return <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>{translate('blockPlayer.previewHint')}</Typography>;
};

/* ───────────────────────── Quiz ───────────────────────── */

interface Choice { text: string; isCorrect?: boolean }

export const QuizPlayer: React.FC<{ blockId: string; data: Record<string, unknown> }> = ({ blockId, data }) => {
    const translate = useTranslate();
    const question = (data.question as string) ?? '';
    const choices = (Array.isArray(data.choices) ? data.choices : []) as Choice[];
    const multiSelect = !!data.multiSelect;
    const explanation = data.explanation as string | undefined;
    const { submit, busy, error, result, reset, canSubmit } = useSubmit(blockId, 'inline_quiz');
    const [selected, setSelected] = useState<number[]>([]);
    const correct = result?.solution?.correctIndices;

    const toggle = (i: number) => {
        if (result) return;
        setSelected((prev) => (multiSelect ? (prev.includes(i) ? prev.filter((x) => x !== i) : [...prev, i]) : [i]));
    };

    return (
        <div className="oa-bp-card oa-bp-interactive" data-block-type="inline_quiz">
            <Head icon={<QuizOutlined fontSize="small" />} title={translate('blockPlayer.quiz.title')} right={multiSelect ? <Chip size="small" label={translate('blockPlayer.quiz.multi')} /> : undefined} />
            <div className="oa-bp-card-body">
                <Typography variant="subtitle1" className="oa-bp-plain" sx={{ fontWeight: 600, mb: 1 }}>{question}</Typography>
                <Stack spacing={0.5} role={multiSelect ? 'group' : 'radiogroup'} aria-label={question}>
                    {choices.map((c, i) => {
                        const isSel = selected.includes(i);
                        const state = correct ? (correct.includes(i) ? 'correct' : isSel ? 'wrong' : '') : '';
                        return (
                            <FormControlLabel
                                key={i}
                                className={`oa-bp-choice${isSel ? ' is-selected' : ''}${state ? ` is-${state}` : ''}`}
                                control={multiSelect ? <Checkbox checked={isSel} onChange={() => toggle(i)} disabled={!!result} /> : <Radio checked={isSel} onChange={() => toggle(i)} disabled={!!result} />}
                                label={<span className="oa-bp-plain">{c.text}</span>}
                            />
                        );
                    })}
                </Stack>
                {error && <Alert severity="error" sx={{ mt: 1 }}>{error}</Alert>}
                <ResultBanner result={result} explanation={explanation} />
                <Stack direction="row" spacing={1} sx={{ mt: 1.5 }}>
                    {!result ? (
                        <Button variant="contained" size="small" disabled={selected.length === 0 || busy || !canSubmit} onClick={() => submit({ selectedIndices: selected })}>
                            {busy ? <CircularProgress size={16} /> : translate('blockPlayer.check')}
                        </Button>
                    ) : (
                        <Button size="small" startIcon={<Replay />} onClick={() => { reset(); setSelected([]); }}>{translate('blockPlayer.retry')}</Button>
                    )}
                </Stack>
                {!canSubmit && <PreviewHint />}
            </div>
        </div>
    );
};

/* ───────────────────────── Fill in the blanks ───────────────────────── */

export const FillBlankPlayer: React.FC<{ blockId: string; data: Record<string, unknown> }> = ({ blockId, data }) => {
    const translate = useTranslate();
    const template = (data.template as string) ?? '';
    const explanation = data.explanation as string | undefined;
    const { submit, busy, error, result, reset, canSubmit } = useSubmit(blockId, 'fill_blank');
    const [answers, setAnswers] = useState<Record<string, string>>({});
    const parts = useMemo(() => template.split(/(\{\{\w+\}\})/g), [template]);
    const blankIds = useMemo(() => (template.match(/\{\{(\w+)\}\}/g) ?? []).map((m) => m.slice(2, -2)), [template]);
    const accepted = result?.solution?.acceptedAnswers;

    return (
        <div className="oa-bp-card oa-bp-interactive" data-block-type="fill_blank">
            <Head icon={<ShortText fontSize="small" />} title={translate('blockPlayer.fillBlank.title')} />
            <div className="oa-bp-card-body">
                <Typography component="p" className="oa-bp-fillblank" sx={{ lineHeight: 2.4 }}>
                    {parts.map((part, i) => {
                        const m = /^\{\{(\w+)\}\}$/.exec(part);
                        if (!m) return <React.Fragment key={i}>{part}</React.Fragment>;
                        const id = m[1];
                        const value = answers[id] ?? '';
                        const ok = accepted ? (accepted[id] ?? []).some((a) => a.trim().toLowerCase() === value.trim().toLowerCase()) : null;
                        return (
                            <TextField
                                key={i}
                                size="small"
                                variant="outlined"
                                value={value}
                                disabled={!!result}
                                onChange={(e) => setAnswers((prev) => ({ ...prev, [id]: e.target.value }))}
                                inputProps={{ 'aria-label': translate('blockPlayer.fillBlank.blank', { n: blankIds.indexOf(id) + 1 }) }}
                                className={`oa-bp-blank${ok === true ? ' is-correct' : ok === false ? ' is-wrong' : ''}`}
                                sx={{ mx: 0.5, width: Math.max(120, Math.min(260, 40 + value.length * 9)) }}
                                helperText={ok === false && accepted ? accepted[id]?.[0] : undefined}
                            />
                        );
                    })}
                </Typography>
                {error && <Alert severity="error" sx={{ mt: 1 }}>{error}</Alert>}
                <ResultBanner result={result} explanation={explanation} />
                <Stack direction="row" spacing={1} sx={{ mt: 1.5 }}>
                    {!result ? (
                        <Button variant="contained" size="small" disabled={busy || !canSubmit || blankIds.some((id) => !(answers[id] ?? '').trim())} onClick={() => submit({ answers })}>
                            {busy ? <CircularProgress size={16} /> : translate('blockPlayer.check')}
                        </Button>
                    ) : (
                        <Button size="small" startIcon={<Replay />} onClick={() => { reset(); setAnswers({}); }}>{translate('blockPlayer.retry')}</Button>
                    )}
                </Stack>
                {!canSubmit && <PreviewHint />}
            </div>
        </div>
    );
};

/* ───────────────────────── Matching ───────────────────────── */

export const MatchingPlayer: React.FC<{ blockId: string; data: Record<string, unknown> }> = ({ blockId, data }) => {
    const translate = useTranslate();
    const explanation = data.explanation as string | undefined;
    const { submit, busy, error, result, reset, canSubmit } = useSubmit(blockId, 'matching');

    // Learner payload (stripped): lefts[{index,text}], rights[{token,text}].
    // Instructor preview (unstripped): pairs[{left,right}] → build the same shape with plain indexes.
    const { lefts, rights } = useMemo(() => {
        if (Array.isArray(data.lefts) && Array.isArray(data.rights)) {
            return { lefts: data.lefts as { index: number; text: string }[], rights: data.rights as { token: string; text: string }[] };
        }
        const pairs = (Array.isArray(data.pairs) ? data.pairs : []) as { left: string; right: string }[];
        return {
            lefts: pairs.map((p, index) => ({ index, text: p.left })),
            rights: pairs.map((p, index) => ({ token: String(index), text: p.right })),
        };
    }, [data]);
    const [matches, setMatches] = useState<Record<number, string>>({});
    const correctMap = useMemo(() => {
        const m: Record<number, string> = {};
        (result?.solution?.correctMatches ?? []).forEach(([left, token]) => { m[left] = token; });
        return m;
    }, [result]);

    return (
        <div className="oa-bp-card oa-bp-interactive" data-block-type="matching">
            <Head icon={<SwapHoriz fontSize="small" />} title={translate('blockPlayer.matching.title')} />
            <div className="oa-bp-card-body">
                <Stack spacing={1}>
                    {lefts.map((l) => {
                        const chosen = matches[l.index] ?? '';
                        const state = result && Object.keys(correctMap).length ? (correctMap[l.index] === chosen ? 'correct' : 'wrong') : '';
                        return (
                            <Stack key={l.index} direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }} className={`oa-bp-matchrow${state ? ` is-${state}` : ''}`}>
                                <Typography variant="body2" sx={{ fontWeight: 600, flex: 1 }}>{l.text}</Typography>
                                <SwapHoriz fontSize="small" sx={{ color: 'var(--oa-text-2)', display: { xs: 'none', sm: 'block' } }} />
                                <Select size="small" displayEmpty value={chosen} disabled={!!result} onChange={(e) => setMatches((prev) => ({ ...prev, [l.index]: String(e.target.value) }))} sx={{ flex: 1, minWidth: 160 }} inputProps={{ 'aria-label': l.text }}>
                                    <MenuItem value=""><em>{translate('blockPlayer.matching.choose')}</em></MenuItem>
                                    {rights.map((r) => <MenuItem key={r.token} value={r.token}>{r.text}</MenuItem>)}
                                </Select>
                                {state === 'wrong' && correctMap[l.index] && (
                                    <Typography variant="caption" color="text.secondary">{translate('blockPlayer.matching.correctIs', { text: rights.find((r) => r.token === correctMap[l.index])?.text ?? '' })}</Typography>
                                )}
                            </Stack>
                        );
                    })}
                </Stack>
                {error && <Alert severity="error" sx={{ mt: 1 }}>{error}</Alert>}
                <ResultBanner result={result} explanation={explanation} />
                <Stack direction="row" spacing={1} sx={{ mt: 1.5 }}>
                    {!result ? (
                        <Button variant="contained" size="small" disabled={busy || !canSubmit || lefts.some((l) => !matches[l.index])} onClick={() => submit({ matches: lefts.map((l) => [l.index, /^\d+$/.test(matches[l.index]) ? Number(matches[l.index]) : matches[l.index]]) })}>
                            {busy ? <CircularProgress size={16} /> : translate('blockPlayer.check')}
                        </Button>
                    ) : (
                        <Button size="small" startIcon={<Replay />} onClick={() => { reset(); setMatches({}); }}>{translate('blockPlayer.retry')}</Button>
                    )}
                </Stack>
                {!canSubmit && <PreviewHint />}
            </div>
        </div>
    );
};

/* ───────────────────────── Ordering ───────────────────────── */

export const OrderingPlayer: React.FC<{ blockId: string; data: Record<string, unknown> }> = ({ blockId, data }) => {
    const translate = useTranslate();
    const explanation = data.explanation as string | undefined;
    const { submit, busy, error, result, reset, canSubmit } = useSubmit(blockId, 'ordering');
    // Stripped payload carries `index` (original position); unstripped preview does not.
    const items = useMemo(() => ((Array.isArray(data.items) ? data.items : []) as { text: string; index?: number }[]).map((it, i) => ({ text: it.text, index: it.index ?? i })), [data]);
    const [order, setOrder] = useState<number[]>(() => items.map((it) => it.index));
    useEffect(() => setOrder(items.map((it) => it.index)), [items]);
    const correctOrder = result?.solution?.correctOrder;

    const move = (pos: number, dir: -1 | 1) => {
        if (result) return;
        const to = pos + dir;
        if (to < 0 || to >= order.length) return;
        setOrder((prev) => {
            const next = [...prev];
            [next[pos], next[to]] = [next[to], next[pos]];
            return next;
        });
    };

    return (
        <div className="oa-bp-card oa-bp-interactive" data-block-type="ordering">
            <Head icon={<SwapVert fontSize="small" />} title={translate('blockPlayer.ordering.title')} />
            <div className="oa-bp-card-body">
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>{translate('blockPlayer.ordering.hint')}</Typography>
                <ol className="oa-bp-orderlist">
                    {order.map((itemIndex, pos) => {
                        const item = items.find((it) => it.index === itemIndex);
                        const state = correctOrder ? (correctOrder[pos] === itemIndex ? 'correct' : 'wrong') : '';
                        return (
                            <li key={itemIndex} className={`oa-bp-orderitem${state ? ` is-${state}` : ''}`}>
                                <span className="oa-bp-index">{pos + 1}</span>
                                <span className="oa-bp-grow">{item?.text}</span>
                                <IconButton size="small" onClick={() => move(pos, -1)} disabled={pos === 0 || !!result} aria-label={translate('blockEditor.actions.moveUp')}><ArrowUpward fontSize="small" /></IconButton>
                                <IconButton size="small" onClick={() => move(pos, 1)} disabled={pos === order.length - 1 || !!result} aria-label={translate('blockEditor.actions.moveDown')}><ArrowDownward fontSize="small" /></IconButton>
                            </li>
                        );
                    })}
                </ol>
                {error && <Alert severity="error" sx={{ mt: 1 }}>{error}</Alert>}
                <ResultBanner result={result} explanation={explanation} />
                <Stack direction="row" spacing={1} sx={{ mt: 1.5 }}>
                    {!result ? (
                        <Button variant="contained" size="small" disabled={busy || !canSubmit} onClick={() => submit({ order })}>
                            {busy ? <CircularProgress size={16} /> : translate('blockPlayer.check')}
                        </Button>
                    ) : (
                        <Button size="small" startIcon={<Replay />} onClick={() => { reset(); setOrder(items.map((it) => it.index)); }}>{translate('blockPlayer.retry')}</Button>
                    )}
                </Stack>
                {!canSubmit && <PreviewHint />}
            </div>
        </div>
    );
};

/* ───────────────────────── Flashcards ───────────────────────── */

export const FlashcardPlayer: React.FC<{ data: Record<string, unknown> }> = ({ data }) => {
    const translate = useTranslate();
    const cards = (Array.isArray(data.cards) ? data.cards : []) as { front: string; back: string }[];
    const [index, setIndex] = useState(0);
    const [flipped, setFlipped] = useState(false);
    if (cards.length === 0) return null;
    const card = cards[Math.min(index, cards.length - 1)];
    const go = (delta: number) => {
        setFlipped(false);
        setIndex((i) => (i + delta + cards.length) % cards.length);
    };
    return (
        <div className="oa-bp-card oa-bp-interactive" data-block-type="flashcard">
            <Head icon={<StyleOutlined fontSize="small" />} title={translate('blockPlayer.flashcard.title')} right={<Typography variant="caption" color="text.secondary">{index + 1} / {cards.length}</Typography>} />
            <div className="oa-bp-card-body">
                <button type="button" className={`oa-bp-flashcard${flipped ? ' is-flipped' : ''}`} onClick={() => setFlipped((f) => !f)} aria-pressed={flipped} aria-label={translate('blockPlayer.flashcard.flip')}>
                    <span className="oa-bp-flashcard-side">{flipped ? card.back : card.front}</span>
                    <span className="oa-bp-flashcard-hint">{translate(flipped ? 'blockPlayer.flashcard.back' : 'blockPlayer.flashcard.front')}</span>
                </button>
                <Stack direction="row" justifyContent="space-between" sx={{ mt: 1.5 }}>
                    <Button size="small" onClick={() => go(-1)} disabled={cards.length < 2}>{translate('blockPlayer.flashcard.prev')}</Button>
                    <Button size="small" variant="outlined" onClick={() => setFlipped((f) => !f)}>{translate('blockPlayer.flashcard.flip')}</Button>
                    <Button size="small" onClick={() => go(1)} disabled={cards.length < 2}>{translate('blockPlayer.flashcard.next')}</Button>
                </Stack>
            </div>
        </div>
    );
};

/* ───────────────────────── Poll ───────────────────────── */

export const PollPlayer: React.FC<{ blockId: string; data: Record<string, unknown> }> = ({ blockId, data }) => {
    const translate = useTranslate();
    const { lessonId, readOnly } = useBlockInteraction();
    const question = (data.question as string) ?? '';
    const options = (Array.isArray(data.options) ? data.options : []) as { text: string }[];
    const multiSelect = !!data.multiSelect;
    const [selected, setSelected] = useState<number[]>([]);
    const [results, setResults] = useState<PollResults | null>(null);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const live = lessonId !== null && !readOnly;

    useEffect(() => {
        if (!live) return;
        let cancelled = false;
        blockInteractionService.pollResults(lessonId!, blockId).then((r) => {
            if (cancelled) return;
            setResults(r);
            setSelected(r.myVote);
        }).catch(() => undefined);
        return () => { cancelled = true; };
    }, [live, lessonId, blockId]);

    const vote = async () => {
        if (!live) return;
        setBusy(true);
        setError(null);
        try {
            setResults(await blockInteractionService.vote(lessonId!, blockId, selected));
        } catch {
            setError(translate('blockPlayer.poll.error'));
        } finally {
            setBusy(false);
        }
    };

    const voted = (results?.myVote.length ?? 0) > 0;
    const showResults = voted && results?.showResults;

    return (
        <div className="oa-bp-card oa-bp-interactive" data-block-type="poll">
            <Head icon={<PollOutlined fontSize="small" />} title={translate('blockPlayer.poll.title')} right={results ? <Typography variant="caption" color="text.secondary">{translate('blockPlayer.poll.voters', { smart_count: results.totalVoters })}</Typography> : undefined} />
            <div className="oa-bp-card-body">
                <Typography variant="subtitle1" className="oa-bp-plain" sx={{ fontWeight: 600, mb: 1 }}>{question}</Typography>
                <Stack spacing={0.75}>
                    {options.map((o, i) => {
                        const isSel = selected.includes(i);
                        const votes = results?.options.find((x) => x.index === i)?.votes ?? 0;
                        const pct = results && results.totalVoters > 0 ? Math.round((votes / results.totalVoters) * 100) : 0;
                        return (
                            <Box key={i} className="oa-bp-polloption">
                                <FormControlLabel
                                    control={multiSelect ? <Checkbox checked={isSel} onChange={() => setSelected((p) => (p.includes(i) ? p.filter((x) => x !== i) : [...p, i]))} /> : <Radio checked={isSel} onChange={() => setSelected([i])} />}
                                    label={o.text}
                                />
                                {showResults && (
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, pl: 4 }}>
                                        <LinearProgress variant="determinate" value={pct} sx={{ flex: 1, height: 8, borderRadius: 4 }} />
                                        <Typography variant="caption" color="text.secondary" sx={{ minWidth: 40 }}>{pct} %</Typography>
                                    </Box>
                                )}
                            </Box>
                        );
                    })}
                </Stack>
                {error && <Alert severity="error" sx={{ mt: 1 }}>{error}</Alert>}
                <Stack direction="row" spacing={1} sx={{ mt: 1.5 }}>
                    <Button variant="contained" size="small" disabled={!live || busy || selected.length === 0} onClick={vote}>
                        {busy ? <CircularProgress size={16} /> : voted ? translate('blockPlayer.poll.change') : translate('blockPlayer.poll.vote')}
                    </Button>
                </Stack>
                {!live && <PreviewHint />}
            </div>
        </div>
    );
};
